/* Dialog for adding a new content source (YouTube, RSS, podcast, Instagram). */

import { useState, useEffect, useCallback, useRef } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  BadgePlus,
  MonitorPlay,
  Podcast,
  FileText,
  Clapperboard,
  Star,
  Loader2,
  HelpCircle,
} from "lucide-react";
import { useSourceNameFetch } from "@/hooks/useSourceNameFetch";
import { useFeatureTracking } from "@/hooks/useFeatureTracking";
import { posthog } from "@/lib/posthog";
import { Events } from "@/lib/posthogEvents";
import { SourceTagSelect } from "@/components/sources/SourceTagSelect";
import { AddBookForm } from "@/components/books/AddBookForm";
import { AddMovieForm } from "@/components/movies/AddMovieForm";
import { AddSingleItemForm } from "@/components/sources/AddSingleItemForm";
import { AddPlaylistForm } from "@/components/playlists/AddPlaylistForm";
import { AddTvShowForm } from "@/components/tv-shows/AddTvShowForm";
import { PodcastSearchForm } from "@/components/sources/PodcastSearchForm";
import { BlogSearchForm } from "@/components/sources/BlogSearchForm";
import { YouTubeSearchForm } from "@/components/sources/YouTubeSearchForm";

type SourceType = "youtube" | "rss" | "podcast" | "instagram";
type ContentType = "all" | "videos" | "podcasts" | "articles" | "shortVideos";
type Mode = "single" | "source" | "book" | "movie" | "tvshow" | "playlist" | "podcast" | "blog" | "youtube";

const SOURCE_TYPE_FEATURE_MAP: Partial<Record<SourceType, string>> = {
  youtube: "youtube_source_added",
  podcast: "podcast_source_added",
  rss: "rss_source_added",
};

const CONTENT_TABS: {
  value: ContentType;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}[] = [
  { value: "all", label: "All" },
  { value: "videos", label: "Videos", icon: MonitorPlay },
  { value: "podcasts", label: "Podcasts", icon: Podcast },
  { value: "articles", label: "Articles", icon: FileText },
  { value: "shortVideos", label: "Short Videos", icon: Clapperboard },
];

const URL_PLACEHOLDERS: Record<ContentType, string> = {
  all: "https://youtube.com/@channel or RSS feed URL",
  videos: "https://youtube.com/@channel",
  podcasts: "https://podcast-rss-feed-url.com/feed",
  articles: "https://blog-or-rss-feed-url.com/feed",
  shortVideos: "https://instagram.com/username",
};

const URL_TOOLTIPS: Record<ContentType, string> = {
  all: "Paste a YouTube channel URL, RSS/podcast feed URL, or Instagram profile URL",
  videos: "Paste a YouTube channel URL (e.g. youtube.com/@channelname)",
  podcasts: "Paste a podcast RSS feed URL",
  articles: "Paste a blog or RSS feed URL",
  shortVideos: "Paste an Instagram profile URL or @username",
};

function contentTypeToSourceType(contentType: ContentType): SourceType | null {
  switch (contentType) {
    case "videos":
      return "youtube";
    case "podcasts":
      return "podcast";
    case "articles":
      return "rss";
    case "shortVideos":
      return "instagram";
    case "all":
      return null;
  }
}

function detectSourceTypeFromUrl(url: string): SourceType | null {
  const lower = url.toLowerCase();
  if (lower.includes("youtube.com") || lower.includes("youtu.be")) {
    return "youtube";
  }
  if (lower.includes("instagram.com")) {
    return "instagram";
  }
  if (url.trim()) {
    return "rss";
  }
  return null;
}

function resolveSourceType(
  contentType: ContentType,
  url: string,
): SourceType | null {
  if (contentType === "all") {
    return detectSourceTypeFromUrl(url);
  }
  return contentTypeToSourceType(contentType);
}

export function AddSourceDialog() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("source");
  const [contentType, setContentType] = useState<ContentType>("all");
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [nameManuallyEdited, setNameManuallyEdited] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [starred, setStarred] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolvedType = resolveSourceType(contentType, url);
  const { fetchedName, isFetching } = useSourceNameFetch(url, resolvedType);

  const createSource = useMutation(api.sources.create);
  const availableTags = useQuery(api.tags.getAvailableTags) ?? [];
  const addTag = useMutation(api.tags.addTag);
  const { trackFeature } = useFeatureTracking();

  useEffect(() => {
    if (fetchedName && !nameManuallyEdited) {
      setName(fetchedName);
    }
  }, [fetchedName, nameManuallyEdited]);

  const resetForm = useCallback(() => {
    setMode("source");
    setContentType("all");
    setUrl("");
    setName("");
    setNameManuallyEdited(false);
    setTags([]);
    setStarred(false);
    setIsSubmitting(false);
    setError(null);
  }, []);

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);
    if (!nextOpen) {
      resetForm();
    }
  }

  function handleContentTypeChange(value: string) {
    setContentType(value as ContentType);
    setUrl("");
    setName("");
    setNameManuallyEdited(false);
    setError(null);
  }

  function handleNameChange(value: string) {
    setName(value);
    setNameManuallyEdited(true);
  }

  async function handleCreateTag(tag: string) {
    await addTag({ tag });
  }

  async function handleSubmit() {
    if (!url.trim() || !name.trim() || !resolvedType) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await createSource({
        name: name.trim(),
        type: resolvedType,
        url: url.trim(),
        tags: tags.length > 0 ? tags : undefined,
        starred: starred || undefined,
      });
      const featureId = SOURCE_TYPE_FEATURE_MAP[resolvedType];
      if (featureId) trackFeature(featureId);
      posthog.capture(Events.SOURCE_ADDED, { sourceType: resolvedType, starred });
      setOpen(false);
      resetForm();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to add source";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  const portalTargetRef = useRef<HTMLDivElement>(null);
  const [footerNode, setFooterNode] = useState<HTMLDivElement | null>(null);

  const canSubmit =
    url.trim().length > 0 &&
    name.trim().length > 0 &&
    resolvedType !== null &&
    !isSubmitting;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon">
          <BadgePlus className="size-6" />
          <span className="sr-only">Add source</span>
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-xl h-[60vh] grid-rows-[auto_1fr_auto]">
        <div ref={portalTargetRef} className="absolute" />
        <DialogHeader className="bg-muted/50 -mx-4 -mt-4 rounded-t-xl border-b p-4">
          <DialogTitle>Add Content</DialogTitle>
        </DialogHeader>

        <Tabs
          value={mode}
          onValueChange={(v) => setMode(v as Mode)}
          className="flex flex-col min-h-0"
        >
          <TabsList variant="default" className="shrink-0">
            <TabsTrigger value="single">Single Item</TabsTrigger>
            <TabsTrigger value="source">Source</TabsTrigger>
            <TabsTrigger value="book">Book</TabsTrigger>
            <TabsTrigger value="movie">Movie</TabsTrigger>
            <TabsTrigger value="tvshow">TV Show</TabsTrigger>
            <TabsTrigger value="playlist">Playlist</TabsTrigger>
            <TabsTrigger value="podcast">Podcast</TabsTrigger>
            <TabsTrigger value="blog">Blog</TabsTrigger>
            <TabsTrigger value="youtube">YouTube</TabsTrigger>
          </TabsList>

          <div className="overflow-y-auto min-h-0">
            <TabsContent value="single">
              <AddSingleItemForm onSuccess={() => { setOpen(false); resetForm(); }} footerContainer={footerNode} />
            </TabsContent>

            <TabsContent value="book">
              <AddBookForm onSuccess={() => { setOpen(false); resetForm(); }} footerContainer={footerNode} />
            </TabsContent>

            <TabsContent value="movie">
              <AddMovieForm onSuccess={() => { setOpen(false); resetForm(); }} footerContainer={footerNode} />
            </TabsContent>

            <TabsContent value="tvshow">
              <AddTvShowForm onSuccess={() => { setOpen(false); resetForm(); }} footerContainer={footerNode} />
            </TabsContent>

            <TabsContent value="playlist">
              <AddPlaylistForm onSuccess={() => { setOpen(false); resetForm(); }} footerContainer={footerNode} />
            </TabsContent>

            <TabsContent value="podcast">
              <PodcastSearchForm onSuccess={() => { setOpen(false); resetForm(); }} footerContainer={footerNode} />
            </TabsContent>

            <TabsContent value="blog">
              <BlogSearchForm onSuccess={() => { setOpen(false); resetForm(); }} footerContainer={footerNode} />
            </TabsContent>

            <TabsContent value="youtube">
              <YouTubeSearchForm onSuccess={() => { setOpen(false); resetForm(); }} footerContainer={footerNode} />
            </TabsContent>

            <TabsContent value="source">
              <SourceForm
                contentType={contentType}
                onContentTypeChange={handleContentTypeChange}
                url={url}
                onUrlChange={(v) => {
                  setUrl(v);
                  setError(null);
                }}
                name={name}
                onNameChange={handleNameChange}
                isFetching={isFetching}
                starred={starred}
                onStarredChange={setStarred}
                tags={tags}
                onTagsChange={setTags}
                availableTags={availableTags}
                onCreateTag={handleCreateTag}
                error={error}
                portalContainer={portalTargetRef}
              />
            </TabsContent>
          </div>
        </Tabs>

        <DialogFooter>
          {mode === "source" ? (
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {isSubmitting && (
                <Loader2 className="size-4 animate-spin" />
              )}
              Add Source
            </Button>
          ) : (
            <div ref={setFooterNode} />
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface SourceFormProps {
  contentType: ContentType;
  onContentTypeChange: (value: string) => void;
  url: string;
  onUrlChange: (value: string) => void;
  name: string;
  onNameChange: (value: string) => void;
  isFetching: boolean;
  starred: boolean;
  onStarredChange: (value: boolean) => void;
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  availableTags: string[];
  onCreateTag: (tag: string) => Promise<void>;
  error: string | null;
  portalContainer: React.RefObject<HTMLElement | null>;
}

function SourceForm({
  contentType,
  onContentTypeChange,
  url,
  onUrlChange,
  name,
  onNameChange,
  isFetching,
  starred,
  onStarredChange,
  tags,
  onTagsChange,
  availableTags,
  onCreateTag,
  error,
  portalContainer,
}: SourceFormProps) {
  const hasUrl = url.trim().length > 0;

  return (
    <div className="space-y-4 pt-2">
      <Tabs value={contentType} onValueChange={onContentTypeChange}>
        <div className="overflow-x-auto scrollbar-none">
          <TabsList variant="line">
            {CONTENT_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.icon && <tab.icon className="size-4" />}
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
      </Tabs>

      <div className="space-y-2">
        <div className="flex items-center gap-1">
          <Label htmlFor="source-url">Source URL</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="size-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top">
                {URL_TOOLTIPS[contentType]}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Input
          id="source-url"
          placeholder={URL_PLACEHOLDERS[contentType]}
          value={url}
          onChange={(e) => onUrlChange(e.target.value)}
          autoComplete="off"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="source-name">Source Name</Label>
        <div className="flex items-center gap-3 rounded-lg border p-3">
          {isFetching && (
            <Loader2 className="size-4 animate-spin text-muted-foreground shrink-0" />
          )}
          <Input
            id="source-name"
            className="flex-1 border-0 p-0 h-auto shadow-none focus-visible:ring-0"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder={hasUrl ? "Fetching name..." : "Enter URL above to auto-detect"}
            disabled={!hasUrl}
          />
          <button
            type="button"
            onClick={() => onStarredChange(!starred)}
            className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            <Star
              className={`size-5 ${starred ? "fill-current text-foreground" : ""}`}
            />
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Tags</Label>
        <SourceTagSelect
          selectedTags={tags}
          onTagsChange={onTagsChange}
          availableTags={availableTags}
          onCreateTag={onCreateTag}
          portalContainer={portalContainer}
        />
      </div>

      {error && (
        <p className="text-destructive text-sm">{error}</p>
      )}
    </div>
  );
}
